import React, { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

/**
 * AppUpdateBanner
 * ----------------
 * Solves the "Samsung S24 Chrome won't pick up new code until I clear site
 * settings" problem.
 *
 * Strategy:
 *   1. We poll `navigator.serviceWorker.registration.update()` every 60s and
 *      on `visibilitychange` (i.e. when the user brings the app to the
 *      foreground). `update()` BYPASSES HTTP cache, so it sees a new sw.js
 *      as soon as one is deployed — even if Chrome's HTTP cache for sw.js
 *      is still "fresh".
 *   2. When a new SW finishes installing, we transition into the "ready to
 *      update" state and show a sticky pill. The SW itself runs
 *      `self.skipWaiting()` on install, so it activates immediately.
 *   3. When the NEW SW takes control (controllerchange fires), we reload the
 *      page automatically so the user gets the new HTML + bundle without a
 *      manual hard refresh.
 *
 * Why a visible pill and not silent auto-reload? Because we don't want to
 * yank the page out from under someone in the middle of logging a workout
 * set. The pill is tappable for instant apply OR auto-applies on next idle
 * navigation.
 */
const POLL_MS = 60_000;

export default function AppUpdateBanner() {
  const [available, setAvailable] = useState(false);
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    let reg;
    let intervalId;

    const triggerReloadOnControllerChange = () => {
      // Fires the moment the new SW takes control of this page.
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloadingRef.current) return;
        reloadingRef.current = true;
        // Tiny delay so any in-flight requests can finish + toast can flash.
        setTimeout(() => window.location.reload(), 200);
      });
    };

    const watchForWaitingSW = (registration) => {
      // Case A: an update is already waiting when we mounted.
      if (registration.waiting) setAvailable(true);

      // Case B: an update arrives later — show the banner AND auto-apply after a
      // short idle delay so users who don't tap the pill still get the new code.
      // The proximate cause of the recurring "I have to clear cache" reports was
      // that users on the OLD bundle never tapped the banner and so never got
      // the new WS-aware page logic. Auto-applying after a 30s delay (or right
      // away on next visibility-change after install) breaks that loop without
      // interrupting an active workout.
      registration.addEventListener("updatefound", () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          if (installing.state === "installed" && navigator.serviceWorker.controller) {
            setAvailable(true);
            // Auto-apply after 30s of idle time. The controllerchange listener
            // above triggers the reload, so this is a single line.
            setTimeout(() => {
              try { registration.waiting && registration.waiting.postMessage("skipWaiting"); } catch { /* ignore */ }
            }, 30_000);
            // Also auto-apply the instant the tab goes hidden (PWA closed /
            // user switched apps) — they won't see the reload at all.
            const onHide = () => {
              if (document.visibilityState === "hidden") {
                try { registration.waiting && registration.waiting.postMessage("skipWaiting"); } catch { /* ignore */ }
                document.removeEventListener("visibilitychange", onHide);
              }
            };
            document.addEventListener("visibilitychange", onHide);
          }
        });
      });
    };

    (async () => {
      try {
        reg = await navigator.serviceWorker.ready;
        watchForWaitingSW(reg);
        triggerReloadOnControllerChange();

        // ── Silent auto-update path ────────────────────────────────────
        // First-visit-after-deploy: if a brand-new SW is already waiting
        // (e.g. the user just opened the app for the first time since we
        // shipped) AND we've never auto-applied an update for this user,
        // silently skip-waiting WITHOUT a banner. The user just sees the
        // fresh app on the next ~200ms reload — feels like no update
        // happened at all. We track this in localStorage so we don't
        // silently reload twice in a row if something goes wrong (which
        // would be a reload loop).
        try {
          const lastSilent = localStorage.getItem("gwg_last_silent_update") || "0";
          const ms = Date.now() - parseInt(lastSilent, 10);
          if (reg.waiting && (ms > 60_000 || lastSilent === "0")) {
            localStorage.setItem("gwg_last_silent_update", String(Date.now()));
            reg.waiting.postMessage("skipWaiting");
          }
        } catch { /* private mode — falls through to manual pill */ }

        // Active polling so we don't wait for Chrome's lazy 24h SW refresh.
        const pump = () => { try { reg.update(); } catch { /* offline / blocked */ } };
        intervalId = setInterval(pump, POLL_MS);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") pump();
        });
        // Initial check on mount (already-online case)
        pump();
      } catch {
        // No SW registered (dev mode / unsupported browser) — silently no-op.
      }
    })();

    return () => { if (intervalId) clearInterval(intervalId); };
  }, []);

  const applyUpdate = () => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) {
      window.location.reload();
      return;
    }
    navigator.serviceWorker.getRegistration().then((reg) => {
      // If we have a waiting worker, tell it to take over now. The
      // controllerchange listener above will then trigger the reload.
      if (reg?.waiting) {
        reg.waiting.postMessage("skipWaiting");
      } else {
        // No waiting worker (rare race) — just reload to fetch fresh HTML.
        window.location.reload();
      }
    }).catch(() => window.location.reload());
  };

  if (!available) return null;

  return (
    <div
      data-testid="app-update-banner"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] px-3"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <button
        data-testid="app-update-apply-btn"
        onClick={applyUpdate}
        className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-display uppercase tracking-wider text-xs px-4 py-2 shadow-lg shadow-blue-500/30 animate-pulse"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        New version available — Tap to refresh
      </button>
    </div>
  );
}
